import { useEffect, useState } from 'react';
import { aed, api, type Coupon } from '../api';

/**
 * Discount codes.
 *
 * Two things this screen has to be honest about, because both cost real money
 * and neither is obvious from a form:
 *
 *  - a code is spent when someone *pays*, not when they open a checkout, so
 *    "3 of 50 used" means three paid bookings;
 *  - a code cannot be deleted, only retired. The bookings that used it keep
 *    the discount they were given — the figure is frozen on each one — and
 *    what was given away stays auditable.
 */
export default function Coupons() {
  const [rows, setRows] = useState<Coupon[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<'percent' | 'amount'>('percent');
  const [value, setValue] = useState('');
  const [expires, setExpires] = useState('');
  const [limit, setLimit] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    api
      .coupons()
      .then(setRows)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(load, []);

  async function create() {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const made = await api.createCoupon({
        code: code.trim().toUpperCase(),
        description: description.trim(),
        percentOff: kind === 'percent' ? Number(value) : null,
        amountOffAed: kind === 'amount' ? Number(value) : null,
        // A date with no time means the end of that day is still valid.
        expiresAt: expires ? new Date(`${expires}T23:59:59`).toISOString() : null,
        maxRedemptions: limit ? Number(limit) : null,
      });
      setNote(`${made.code} is live.`);
      setCode('');
      setDescription('');
      setValue('');
      setExpires('');
      setLimit('');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const valid = code.trim().length >= 3 && Number(value) > 0 && (kind !== 'percent' || Number(value) <= 100);

  return (
    <>
      <h1>Coupons</h1>
      <p className="sub">
        A code comes off the safari price only — never off the Abu Dhabi pickup fee or the add-ons —
        and it stacks with the automatic last-minute discount. Uses are counted when a booking is
        paid, not when someone opens the checkout.
      </p>

      <div className="card grid">
        <h2 style={{ marginBlockStart: 0 }}>New code</h2>
        <div className="row">
          <input
            aria-label="Code"
            placeholder="FAMILY10"
            value={code}
            onChange={(e) => setCode(e.currentTarget.value.toUpperCase())}
            style={{ width: '10rem', textTransform: 'uppercase' }}
          />
          <select aria-label="Kind" value={kind} onChange={(e) => setKind(e.currentTarget.value as 'percent' | 'amount')}>
            <option value="percent">% off</option>
            <option value="amount">AED off</option>
          </select>
          <input
            aria-label={kind === 'percent' ? 'Percent off' : 'Amount off in AED'}
            type="number"
            min={1}
            max={kind === 'percent' ? 100 : undefined}
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            style={{ width: '7rem' }}
          />
          <input
            aria-label="Expires"
            type="date"
            value={expires}
            onChange={(e) => setExpires(e.currentTarget.value)}
            title="Last day the code works. Leave empty for no expiry."
          />
          <input
            aria-label="Maximum uses"
            type="number"
            min={1}
            placeholder="Uses (any)"
            value={limit}
            onChange={(e) => setLimit(e.currentTarget.value)}
            style={{ width: '8rem' }}
          />
          <input
            aria-label="What it is for"
            placeholder="What it is for (internal)"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            style={{ flex: 1, minWidth: '12rem' }}
          />
          <button className="primary" disabled={busy || !valid} onClick={() => void create()}>
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
        {note && <p className="ok">{note}</p>}
        {error && <p className="err">{error}</p>}
      </div>

      {!rows && <p className="muted">Loading…</p>}
      {rows && rows.length === 0 && <p className="muted">No codes yet.</p>}
      {rows && rows.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Worth</th>
              <th>Used</th>
              <th>Expires</th>
              <th>For</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.code} className={c.active ? undefined : 'muted'}>
                <td>
                  <strong>{c.code}</strong>
                  {!c.active && <div className="small muted">retired</div>}
                </td>
                <td className="num">{c.percentOff !== null ? `${c.percentOff}%` : aed(c.amountOffAed)}</td>
                <td className="num">
                  {c.timesRedeemed}
                  {c.maxRedemptions !== null && ` of ${c.maxRedemptions}`}
                </td>
                <td className="num">
                  {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString('en-GB') : '—'}
                </td>
                <td className="small muted">{c.description || '—'}</td>
                <td>
                  {c.active && (
                    <button
                      onClick={() => {
                        if (
                          confirm(
                            `Retire ${c.code}?\n\n` +
                              'Nobody can use it again. Bookings that already used it keep their ' +
                              'discount — the amount is frozen on each one.',
                          )
                        ) {
                          void api
                            .deactivateCoupon(c.code)
                            .then(load)
                            .catch((e) => setError(e instanceof Error ? e.message : String(e)));
                        }
                      }}
                    >
                      Retire
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
